/**
 * Modo Ponte na conversa: a IA está conversando com a cliente. A vendedora
 * vê o que ela já entendeu e assume com um clique (ou só respondendo).
 */
import type { ConversaResumo } from "@ydchat/shared";
import { Bot } from "lucide-react";
import { Botao } from "@/components/ui/botao";
import { useAcoesPonte } from "@/lib/atendimento";

export function FaixaPonte({ conversa }: { conversa: ConversaResumo }) {
  const { assumir } = useAcoesPonte(conversa.id);
  const conduzindo = conversa.modo === "ponte" || conversa.modo === "fora_horario";
  const retomar = !conduzindo && !!conversa.retomarDesde;
  if (!conduzindo && !retomar) return null;
  if (retomar && !conversa.ponte) return null;
  const p = conversa.ponte;
  const campos = p
    ? (
        [
          ["Procura", p.procura],
          ["Ocasião", p.ocasiao],
          ["Tamanho", p.tamanho],
          ["Cores", p.cores],
          ["Para quando", p.prazo],
          ["Orçamento", p.orcamento],
        ] as const
      ).filter(([, v]) => v)
    : [];
  return (
    <section
      aria-label="Modo Ponte"
      className="mx-auto mt-2 max-w-3xl rounded-xl bg-gold-50 p-3 ring-1 ring-gold-200 max-md:mx-3"
    >
      <div className="flex items-start gap-2.5">
        <Bot size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-gold-700" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-gold-800">
            {retomar
              ? "Escreveu com a loja fechada — o que a assistente virtual entendeu"
              : conversa.modo === "fora_horario"
                ? "Loja fechada: a assistente virtual está recebendo a cliente"
                : "A assistente virtual está conversando com a cliente"}
          </p>
          <p className="text-[13px] text-neutral-700">
            {p?.resumo ??
              (conversa.modo === "fora_horario"
                ? "Se a IA estiver desligada, a cliente recebeu a mensagem de ausência."
                : "Ela acabou de entrar; o resumo aparece aqui.")}
          </p>
          {!!campos.length && (
            <dl className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
              {campos.map(([k, v]) => (
                <div key={k} className="flex gap-1">
                  <dt className="text-neutral-500">{k}:</dt>
                  <dd className="font-semibold text-neutral-800">{v}</dd>
                </div>
              ))}
            </dl>
          )}
          {!!p?.produtosSugeridos.length && (
            <p className="mt-1 text-xs text-neutral-600">
              Já mostrou: {p.produtosSugeridos.join(", ")}
            </p>
          )}
          <p className="mt-1 text-[11px] text-neutral-500">
            {retomar
              ? "Responda para tirar da fila de retomada."
              : "Ao responder, você assume e a IA sai."}
          </p>
        </div>
        {conduzindo && (
          <Botao
            tamanho="sm"
            variante="secundario"
            onClick={() => assumir.mutate()}
            disabled={assumir.isPending}
          >
            Assumir
          </Botao>
        )}
      </div>
    </section>
  );
}
